import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Files, Store as StoreIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const SAME = "__same__";

/**
 * DuplicateGameDialog — admin-only dialog to clone a game.
 *
 * Lets the admin optionally re-assign the copy to another merchant
 * (defaults to the source game's merchant). On confirm, calls `onConfirm`
 * with the chosen merchant id (or null to keep the same one).
 *
 * The body is unmounted when `open` is false so internal state resets
 * naturally between sessions — no useEffect needed.
 */
export default function DuplicateGameDialog({
  open,
  onOpenChange,
  game,
  merchants = [],
  onConfirm,
  loading = false,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="duplicate-game-dialog"
      >
        {open && game && (
          <DialogBody
            game={game}
            merchants={merchants}
            onConfirm={onConfirm}
            onCancel={() => onOpenChange(false)}
            loading={loading}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function DialogBody({ game, merchants, onConfirm, onCancel, loading }) {
  const { t } = useTranslation();
  const [merchantId, setMerchantId] = useState(SAME);

  const sourceMerchant = merchants.find((m) => m.id === game.merchant_id);
  const targetMerchant =
    merchantId === SAME
      ? sourceMerchant
      : merchants.find((m) => m.id === merchantId);
  const willSwitchMerchant =
    merchantId !== SAME && merchantId !== game.merchant_id;

  const handleConfirm = async () => {
    const targetId = merchantId === SAME ? null : merchantId;
    await onConfirm(targetId);
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Files className="h-4 w-4 text-amber-600" />
          {t("dashboard.duplicate_dialog_title", "Dupliquer ce jeu")}
        </DialogTitle>
        <DialogDescription className="text-[12.5px] leading-relaxed">
          <span className="block">
            <span className="font-semibold text-foreground">{game.title}</span>
          </span>
          {t(
            "dashboard.duplicate_dialog_body",
            "La copie sera créée désactivée, prête à être ajustée. Lots et CTAs conservés ; participants et gagnants non copiés.",
          )}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-3 py-1">
        <div>
          <label className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
            {t("dashboard.duplicate_assign_to", "Assigner à l'enseigne")}
          </label>
          <Select value={merchantId} onValueChange={setMerchantId}>
            <SelectTrigger
              className="h-10 rounded-lg mt-1"
              data-testid="duplicate-merchant-select"
            >
              <SelectValue
                placeholder={t(
                  "dashboard.duplicate_select_merchant",
                  "Choisir l'enseigne",
                )}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SAME}>
                {t("dashboard.duplicate_same_merchant", "Même enseigne")}
                {sourceMerchant ? ` — ${sourceMerchant.name}` : ""}
              </SelectItem>
              {merchants
                .filter((m) => m.id !== game.merchant_id)
                .map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          {willSwitchMerchant && targetMerchant && (
            <div className="mt-2 flex items-start gap-2 rounded-lg bg-amber-500/10 text-amber-700 dark:text-amber-300 px-3 py-2 text-[11.5px]">
              <StoreIcon className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <div>
                {t(
                  "dashboard.duplicate_merchant_switch_warning",
                  "La copie sera assignée à",
                )}{" "}
                <span className="font-semibold">{targetMerchant.name}</span>
                {". "}
                {t(
                  "dashboard.duplicate_merchant_switch_note",
                  "Pense à ajuster le règlement, les CTAs et les codes coupons si nécessaire.",
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <DialogFooter className="gap-2">
        <Button
          variant="outline"
          onClick={onCancel}
          disabled={loading}
          data-testid="duplicate-cancel-btn"
        >
          {t("common.cancel", "Annuler")}
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={loading}
          className="bg-amber-600 hover:bg-amber-700 text-white"
          data-testid="duplicate-confirm-btn"
        >
          <Files className="h-3.5 w-3.5 mr-1.5" />
          {loading
            ? t("common.loading", "Chargement…")
            : t("dashboard.duplicate_confirm_btn", "Dupliquer")}
        </Button>
      </DialogFooter>
    </>
  );
}
